import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faSpinner, 
  faArchive,
  faUndo,
  faUser,
  faCalendarAlt,
  faUserTag,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const ArchiveControllers = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [archivedControllers, setArchivedControllers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [controllerToRestore, setControllerToRestore] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchArchivedControllers();
  }, []);

  const fetchArchivedControllers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/controllers/archived');
      if (response.data.success) {
        setArchivedControllers(response.data.data);
      } else {
        setError('Erreur lors du chargement des contrôleurs archivés');
      }
    } catch (error) {
      console.error('Error fetching archived controllers:', error);
      setError('Erreur lors du chargement des contrôleurs archivés');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (controller) => {
    setControllerToRestore(controller);
    setShowConfirmModal(true);
  };

  const handleRestore = async () => {
    if (!controllerToRestore) return;
    
    try {
      setRestoring(controllerToRestore.id);
      setShowConfirmModal(false);
      
      // `source` tells the API which table this row came from: a soft-archived controller is
      // restored in place, while a legacy row has to be re-inserted on its original id.
      await api.post(`/controllers/archived/${controllerToRestore.id}/restore`, {
        source: controllerToRestore.source || 'controller',
      });
      fetchArchivedControllers(); // Refresh the list
      
      setSuccessMessage('Contrôleur restauré avec succès!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error restoring controller:', error);
      setSuccessMessage(error.response?.data?.message || 'Erreur lors de la restauration du contrôleur');
      setShowSuccessModal(true);
    } finally {
      setRestoring(null);
      setControllerToRestore(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des contrôleurs archivés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">CONTRÔLEURS ARCHIVÉS</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {archivedControllers.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 sm:p-8 text-center text-gray-500">
            <FontAwesomeIcon icon={faArchive} className="text-4xl text-gray-300 mb-4" />
            <p className="text-lg">Aucun contrôleur archivé</p>
            <p className="text-sm">Les contrôleurs archivés apparaîtront ici</p>
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {/* Mobile Card View */}
            <div className="block sm:hidden">
              {archivedControllers.map((controller) => (
                <div key={controller.id} className="border-b border-gray-200 p-4 last:border-b-0">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faUser} className="text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{controller.name}</div>
                      <div className="text-xs text-gray-500 truncate">{controller.email}</div>
                      <div className="text-xs text-gray-400">ID: {controller.original_id}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Spécialisation:</span>
                      <span className="text-gray-900">{controller.specialization || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Statut:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${controller.status_color}`}>
                        {controller.status_text}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Archivé le:</span>
                      <span className="text-gray-900 text-xs">{formatDate(controller.archived_at)}</span>
                    </div>
                    <div className="flex items-start justify-between">
                      <span className="text-gray-600">Raison:</span>
                      <span className="text-gray-900 text-xs text-right max-w-[60%]">{controller.archive_reason}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Archivé par:</span>
                      <span className="text-gray-900 text-xs">{controller.archived_by}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleRestoreClick(controller)}
                        disabled={restoring === controller.id}
                        className="w-full px-4 py-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {restoring === controller.id ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            <span>Restauration...</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUndo} />
                            <span>Restaurer</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contrôleur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Spécialisation
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archivé le
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Raison
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archivé par
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {archivedControllers.map((controller) => (
                    <tr key={controller.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <FontAwesomeIcon icon={faUser} className="text-red-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{controller.name}</div>
                            <div className="text-sm text-gray-500 truncate">{controller.email}</div>
                            <div className="text-xs text-gray-400">ID: {controller.original_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{controller.specialization || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${controller.status_color}`}>
                          {controller.status_text}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          {formatDate(controller.archived_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={controller.archive_reason}>
                          {controller.archive_reason}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <FontAwesomeIcon icon={faUserTag} className="mr-1" />
                          {controller.archived_by}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRestoreClick(controller)}
                          disabled={restoring === controller.id}
                          className="text-green-600 hover:text-green-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {restoring === controller.id ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                              Restauration...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faUndo} className="mr-1" />
                              Restaurer
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleRestore}
        title="Confirmer la restauration"
        message={`Êtes-vous sûr de vouloir restaurer ${controllerToRestore?.name || 'ce contrôleur'} ?`}
        confirmText="Restaurer"
        cancelText="Annuler"
        isLoading={restoring !== null}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Opération terminée"
        message={successMessage}
      />
    </div>
  );
};

export default ArchiveControllers;
